import { Fragment, useState, useCallback } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { useDropzone, type FileRejection } from 'react-dropzone';
import {
  XMarkIcon,
  CloudArrowUpIcon,
  DocumentIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
} from '@heroicons/react/24/outline';
import type {
  NIBSourceSystem,
  NIBPayIndicator,
  NIBImportResult,
  NIBMergeResult,
  NIBNewHire,
} from '@/types';
import { NIB_PAY_INDICATOR_LABELS } from '@/types';

type ImportMode = 'import' | 'merge';

interface ImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onImport: (
    file: File,
    sourceSystem: NIBSourceSystem,
    payIndicator: NIBPayIndicator
  ) => Promise<NIBImportResult | NIBMergeResult>;
  mode?: ImportMode;
  submissionId?: string;
  isLoading?: boolean;
  onApproveNewHires?: (
    newHires: NIBNewHire[],
    addToRoster: boolean
  ) => Promise<void>;
}

const ACCEPTED_FILE_TYPES = {
  'text/csv': ['.csv'],
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': [
    '.xlsx',
  ],
  'application/vnd.ms-excel': ['.xls'],
};

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

const SOURCE_SYSTEMS: { label: string; value: NIBSourceSystem }[] = [
  { label: 'QuickBooks', value: 'QUICKBOOKS' },
  { label: 'ADP', value: 'ADP' },
];

const PAY_INDICATORS: { label: string; value: NIBPayIndicator }[] = [
  { label: NIB_PAY_INDICATOR_LABELS.W, value: 'W' },
  { label: NIB_PAY_INDICATOR_LABELS.B, value: 'B' },
  { label: NIB_PAY_INDICATOR_LABELS.S, value: 'S' },
  { label: NIB_PAY_INDICATOR_LABELS.M, value: 'M' },
];

export function ImportModal({
  isOpen,
  onClose,
  onImport,
  mode = 'import',
  isLoading = false,
  onApproveNewHires,
}: ImportModalProps) {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string>('');
  const [sourceSystem, setSourceSystem] =
    useState<NIBSourceSystem>('QUICKBOOKS');
  const [payIndicator, setPayIndicator] = useState<NIBPayIndicator>('W');
  const [importResult, setImportResult] = useState<
    NIBImportResult | NIBMergeResult | null
  >(null);
  const [importing, setImporting] = useState(false);

  // New hire approval state
  const [selectedNewHires, setSelectedNewHires] = useState<Set<string>>(new Set());
  const [addToRoster, setAddToRoster] = useState(false);
  const [approvingNewHires, setApprovingNewHires] = useState(false);

  const isMerge = mode === 'merge';
  const title = isMerge ? 'Merge Wages from Payroll' : 'Import Employee Records';
  const actionLabel = isMerge ? 'Merge Wages' : 'Import Records';
  const loadingLabel = isMerge ? 'Merging...' : 'Importing...';

  // Derive new hires list from merge result
  const mergeResult = importResult && 'matched' in importResult ? importResult : null;
  const newHires = mergeResult?.new_hires ?? [];

  const toggleNewHire = (niNumber: string) => {
    setSelectedNewHires(prev => {
      const next = new Set(prev);
      if (next.has(niNumber)) {
        next.delete(niNumber);
      } else {
        next.add(niNumber);
      }
      return next;
    });
  };

  const toggleAllNewHires = () => {
    if (selectedNewHires.size === newHires.length) {
      setSelectedNewHires(new Set());
    } else {
      setSelectedNewHires(new Set(newHires.map(h => h.ni_number)));
    }
  };

  const handleApproveNewHires = async () => {
    if (!onApproveNewHires || selectedNewHires.size === 0) return;
    const selected = newHires.filter(h => selectedNewHires.has(h.ni_number));
    setApprovingNewHires(true);
    try {
      await onApproveNewHires(selected, addToRoster);
    } catch (error) {
      console.error('Failed to add new hires:', error);
    } finally {
      setApprovingNewHires(false);
    }
  };

  const onDrop = useCallback(
    (acceptedFiles: File[], rejectedFiles: FileRejection[]) => {
      if (rejectedFiles.length > 0) {
        const error = rejectedFiles[0]?.errors[0]?.message || 'File rejected';
        setFileError(error);
        setSelectedFile(null);
        return;
      }

      if (acceptedFiles.length > 0) {
        setSelectedFile(acceptedFiles[0] || null);
        setFileError('');
        setImportResult(null);
      }
    },
    []
  );

  const { getRootProps, getInputProps, isDragActive, isDragReject } =
    useDropzone({
      onDrop,
      accept: ACCEPTED_FILE_TYPES,
      maxSize: MAX_FILE_SIZE,
      maxFiles: 1,
      disabled: isLoading || importing,
    });

  const handleImport = async () => {
    if (!selectedFile) {
      setFileError('Please select a file');
      return;
    }

    setImporting(true);
    try {
      const result = await onImport(selectedFile, sourceSystem, payIndicator);
      setImportResult(result);
    } catch (error) {
      console.error('Import error:', error);
      setFileError('Failed to import file. Please try again.');
    } finally {
      setImporting(false);
    }
  };

  const handleClose = () => {
    setSelectedFile(null);
    setFileError('');
    setImportResult(null);
    setImporting(false);
    setSelectedNewHires(new Set());
    setAddToRoster(false);
    setApprovingNewHires(false);
    onClose();
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={handleClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-2xl transform overflow-hidden rounded-2xl bg-white text-left align-middle shadow-xl transition-all">
                <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                  <Dialog.Title
                    as="h3"
                    className="text-lg font-medium leading-6 text-gray-900"
                  >
                    {title}
                  </Dialog.Title>
                  <button
                    type="button"
                    onClick={handleClose}
                    className="text-gray-400 hover:text-gray-500"
                    disabled={importing}
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>

                <div className="px-6 py-6 space-y-6">
                  {!importResult ? (
                    <>
                      {/* Source System Selector */}
                      <div>
                        <label
                          htmlFor="source-system"
                          className="block text-sm font-medium text-gray-700 mb-2"
                        >
                          Source System
                        </label>
                        <select
                          id="source-system"
                          value={sourceSystem}
                          onChange={e =>
                            setSourceSystem(e.target.value as NIBSourceSystem)
                          }
                          disabled={importing}
                          className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md disabled:bg-gray-100 disabled:cursor-not-allowed"
                        >
                          {SOURCE_SYSTEMS.map(system => (
                            <option key={system.value} value={system.value}>
                              {system.label}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* Pay Indicator Selector */}
                      <div>
                        <label
                          htmlFor="pay-indicator"
                          className="block text-sm font-medium text-gray-700 mb-2"
                        >
                          Pay Indicator
                        </label>
                        <select
                          id="pay-indicator"
                          value={payIndicator}
                          onChange={e =>
                            setPayIndicator(e.target.value as NIBPayIndicator)
                          }
                          disabled={importing}
                          className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md disabled:bg-gray-100 disabled:cursor-not-allowed"
                        >
                          {PAY_INDICATORS.map(indicator => (
                            <option
                              key={indicator.value}
                              value={indicator.value}
                            >
                              {indicator.label}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* File Dropzone */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Select File
                        </label>
                        <div
                          {...getRootProps()}
                          className={`
                            relative border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors
                            ${isDragActive && !isDragReject ? 'border-blue-500 bg-blue-50' : ''}
                            ${isDragReject ? 'border-red-500 bg-red-50' : ''}
                            ${!isDragActive && !isDragReject ? 'border-gray-300 hover:border-gray-400' : ''}
                            ${importing ? 'opacity-50 cursor-not-allowed' : ''}
                          `}
                        >
                          <input {...getInputProps()} />
                          <CloudArrowUpIcon className="mx-auto h-12 w-12 text-gray-400" />
                          <p className="mt-2 text-sm text-gray-600">
                            {isDragActive ? (
                              isDragReject ? (
                                <span className="text-red-600">
                                  File type not accepted
                                </span>
                              ) : (
                                <span className="text-blue-600">
                                  Drop file here...
                                </span>
                              )
                            ) : (
                              <>
                                <span className="font-semibold">
                                  Click to upload
                                </span>{' '}
                                or drag and drop
                              </>
                            )}
                          </p>
                          <p className="mt-1 text-xs text-gray-500">
                            CSV, XLSX, or XLS up to 10MB
                          </p>
                        </div>
                      </div>

                      {/* Selected File Display */}
                      {selectedFile && (
                        <div className="flex items-center justify-between p-4 rounded-lg border border-gray-200 bg-white">
                          <div className="flex items-center space-x-3 flex-1 min-w-0">
                            <DocumentIcon className="h-8 w-8 text-blue-500 flex-shrink-0" />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900 truncate">
                                {selectedFile.name}
                              </p>
                              <p className="text-xs text-gray-500">
                                {formatFileSize(selectedFile.size)}
                              </p>
                            </div>
                          </div>
                          <CheckCircleIcon className="h-5 w-5 text-green-500 flex-shrink-0" />
                        </div>
                      )}

                      {/* Error Display */}
                      {fileError && (
                        <div className="flex items-start p-4 bg-red-50 border border-red-200 rounded-lg">
                          <ExclamationCircleIcon className="h-5 w-5 text-red-500 mt-0.5 mr-3 flex-shrink-0" />
                          <p className="text-sm text-red-800">{fileError}</p>
                        </div>
                      )}
                    </>
                  ) : isMerge && 'matched' in importResult ? (
                    /* Merge Results */
                    <div className="space-y-4">
                      <div className="flex items-center p-4 bg-green-50 border border-green-200 rounded-lg">
                        <CheckCircleIcon className="h-6 w-6 text-green-500 mr-3" />
                        <div>
                          <p className="text-sm font-medium text-green-900">
                            Payroll merge completed!
                          </p>
                          <p className="text-xs text-green-700 mt-1">
                            {importResult.matched} of {importResult.total_in_file} employees matched and wages updated
                          </p>
                        </div>
                      </div>

                      {newHires.length > 0 && (
                        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                          <div className="flex items-center justify-between mb-3">
                            <p className="text-sm font-medium text-blue-900">
                              {newHires.length} new employee{newHires.length !== 1 ? 's' : ''} found in payroll (not in roster):
                            </p>
                            <label className="flex items-center text-xs text-blue-700 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={selectedNewHires.size === newHires.length && newHires.length > 0}
                                onChange={toggleAllNewHires}
                                className="rounded border-blue-300 text-blue-600 focus:ring-blue-500 mr-1.5"
                              />
                              Select All
                            </label>
                          </div>
                          <div className="max-h-40 overflow-y-auto space-y-1.5">
                            {newHires.map(hire => (
                              <label
                                key={hire.ni_number}
                                className="flex items-center p-1.5 rounded hover:bg-blue-100 cursor-pointer transition-colors"
                              >
                                <input
                                  type="checkbox"
                                  checked={selectedNewHires.has(hire.ni_number)}
                                  onChange={() => toggleNewHire(hire.ni_number)}
                                  className="rounded border-blue-300 text-blue-600 focus:ring-blue-500 mr-2.5 flex-shrink-0"
                                />
                                <span className="text-xs text-blue-700">
                                  {hire.ni_number} — {hire.last_name}, {hire.first_name} (${hire.original_gross_wages.toFixed(2)})
                                </span>
                              </label>
                            ))}
                          </div>
                          {onApproveNewHires && (
                            <div className="mt-3 pt-3 border-t border-blue-200 flex items-center justify-between">
                              <label className="flex items-center text-xs text-blue-700 cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={addToRoster}
                                  onChange={e => setAddToRoster(e.target.checked)}
                                  className="rounded border-blue-300 text-blue-600 focus:ring-blue-500 mr-1.5"
                                />
                                Also add to employer roster
                              </label>
                              <button
                                type="button"
                                onClick={handleApproveNewHires}
                                disabled={selectedNewHires.size === 0 || approvingNewHires}
                                className="px-3 py-1.5 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                {approvingNewHires
                                  ? 'Adding...'
                                  : `Add ${selectedNewHires.size > 0 ? selectedNewHires.size + ' ' : ''}Selected Employee${selectedNewHires.size !== 1 ? 's' : ''}`}
                              </button>
                            </div>
                          )}
                        </div>
                      )}

                      {importResult.missing_from_file.length > 0 && (
                        <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                          <p className="text-sm font-medium text-yellow-900 mb-2">
                            {importResult.missing_from_file.length} roster employee{importResult.missing_from_file.length !== 1 ? 's' : ''} not in payroll file (wages remain $0):
                          </p>
                          <div className="max-h-32 overflow-y-auto space-y-1">
                            {importResult.missing_from_file.map((emp, index) => (
                              <p key={index} className="text-xs text-yellow-700">
                                {emp.ni_number} — {emp.name}
                              </p>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    /* Import Results (original) */
                    <div className="space-y-4">
                      <div className="flex items-center p-4 bg-green-50 border border-green-200 rounded-lg">
                        <CheckCircleIcon className="h-6 w-6 text-green-500 mr-3" />
                        <div>
                          <p className="text-sm font-medium text-green-900">
                            Import completed successfully!
                          </p>
                          <p className="text-xs text-green-700 mt-1">
                            {'imported_count' in importResult && importResult.imported_count} of{' '}
                            {'total_rows' in importResult && importResult.total_rows} records imported
                          </p>
                        </div>
                      </div>

                      {'skipped_count' in importResult && importResult.skipped_count > 0 && (
                        <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                          <p className="text-sm font-medium text-yellow-900">
                            {importResult.skipped_count} records were skipped
                          </p>
                        </div>
                      )}

                      {'errors' in importResult && importResult.errors.length > 0 && (
                        <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                          <p className="text-sm font-medium text-red-900 mb-2">
                            Import Errors:
                          </p>
                          <div className="max-h-48 overflow-y-auto space-y-1">
                            {importResult.errors.map((error, index) => (
                              <p key={index} className="text-xs text-red-700">
                                Row {error.row}: {error.message}
                                {error.field && ` (${error.field})`}
                              </p>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end space-x-3">
                  {!importResult ? (
                    <>
                      <button
                        type="button"
                        onClick={handleClose}
                        disabled={importing}
                        className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={handleImport}
                        disabled={!selectedFile || importing}
                        className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {importing ? (
                          <>
                            <svg
                              className="animate-spin -ml-1 mr-2 h-4 w-4 text-white inline-block"
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                            >
                              <circle
                                className="opacity-25"
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                strokeWidth="4"
                              ></circle>
                              <path
                                className="opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                              ></path>
                            </svg>
                            {loadingLabel}
                          </>
                        ) : (
                          actionLabel
                        )}
                      </button>
                    </>
                  ) : (
                    <button
                      type="button"
                      onClick={handleClose}
                      className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      Done
                    </button>
                  )}
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
